import * as React from "react";
import * as GoogleMapsApi from "@react-google-maps/api";

interface GoogleMapsContextType {
  isLoaded: boolean;
  loadError: Error | undefined;
}

const GoogleMapsContext = React.createContext<
  GoogleMapsContextType | undefined
>(undefined);

export const useGoogleMaps = () => {
  const context = React.useContext(GoogleMapsContext);
  if (context === undefined) {
    throw new Error("useGoogleMaps must be used within a GoogleMapsProvider");
  }
  return context;
};

export const GoogleMapsProvider: React.FC<React.PropsWithChildren<{}>> = ({
  children,
}) => {
  const [isLoaded, setIsLoaded] = React.useState(false);
  const [loadError, setLoadError] = React.useState<Error | undefined>(
    undefined
  );

  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;

  // Define the libraries array with valid Google Maps library strings
  const libraries = ["places", "marker"] as (
    | "places"
    | "drawing"
    | "geometry"
    | "marker"
  )[];

  return (
    <GoogleMapsContext.Provider value={{ isLoaded, loadError }}>
      <GoogleMapsApi.LoadScript
        googleMapsApiKey={apiKey || ""}
        libraries={libraries} // Using a valid string array directly
        onLoad={() => setIsLoaded(true)}
        onError={() => setLoadError(new Error("Error loading Google Maps API"))}
      >
        {children}
      </GoogleMapsApi.LoadScript>
    </GoogleMapsContext.Provider>
  );
};
